// MIDAS V3 — Composer ☰ menu (COMPOSER-MENU, 2026-07-15)
//
// Declutters the composer input row: the per-thread MODEL picker
// (ModelPickerV3) moves out of the row into this compact popover, opened by a
// single ☰ hamburger button that sits to the LEFT of the ＋ attach button.
// The freed row space goes to the textarea. PURELY a relocation — the hosted
// picker keeps its exact state key (midasV3.modelOverrideByThread keyed by
// the composer's `threadKey`), per-column semantics (this component mounts
// once per ChatInputAreaV3 instance, i.e. primary + each attached column),
// and send-path stamping.
//
// BUG 11 (owner ruling 2026-10-02): the answer-PIPELINE section (v1/v2
// opt-out pills + "Default: v3" helper) was REMOVED. The client never sends
// `pipeline_override`; the bot gateway runs v3 for every turn.
//
// Structure of the popover (anchored ABOVE the trigger — composer is pinned
// to the viewport bottom — right-aligned, portal to document.body re-wrapped
// in `.midas-v3-root p-{theme}` so V3 custom properties resolve; same pattern
// as ModelPickerV3/RailRowMenuV3):
//   MODEL · THIS THREAD     (eyebrow)
//   [Sonnet 5 ▾]            (ModelPickerV3 — opens its own nested menu)
//
// Nested-menu handling: ModelPickerV3 portals ITS dropdown to document.body
// (outside this popover's DOM), tagged `data-midas-v3-submenu`. Our
// outside-mousedown + Esc handlers skip events originating inside that tag so
// the nested menu works: first Esc / outside-click closes the submenu (its
// own handlers), the next closes this popover. The submenu's zIndex (1400)
// sits ABOVE this popover (1390) by design.
//
// Override indicator: a small accent dot on the ☰ trigger whenever this
// thread has a non-default model — the user can see an override is in effect
// without opening the menu.
//
// `.cc` ONLY (isCcDevSurface): renders null off-`.cc` — the composer row is
// pixel-identical off-`.cc`, exactly like the controls it replaced.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { resolveV3PaletteClass } from "../../midasV3Palette";
import ModelPickerV3 from "./ModelPickerV3";
import { isCcDevSurface } from "./ccSurface";

export interface ComposerMenuV3Props {
  /** Per-thread selection key — activeThreadId ?? "draft" (primary) or the
   *  attached column's threadId. Passed through UNCHANGED to the hosted
   *  model picker (thread_id-per-column hard rule untouched). */
  threadKey: string;
  disabled?: boolean;
}

const MENU_WIDTH = 264;
const MENU_OFFSET_Y = 8;
const VIEWPORT_MARGIN = 8;

/** Same eyebrow style as ModelPickerV3's menu headers — JetBrains Mono, 10px,
 *  uppercase, faint, non-interactive. */
const EYEBROW_STYLE: React.CSSProperties = {
  padding: "5px 12px 4px",
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--midas-v3-faint)",
};

// Palette for the body portal: `resolveV3PaletteClass()` (midasV3Palette.ts)
// replaced a local 2-way DOM sniff that fell through to `p-machineid` for any
// palette other than claude — brand-blue popover on a teal/slate page
// (MIDAS-V3-PALETTES-TEAL-SLATE).

/** ☰ — three-line hamburger glyph as an inline SVG (crisper than the text
 *  glyph at 30px, consistent stroke with the picker chevron). */
const HamburgerIcon: React.FC = () => (
  <svg
    viewBox="0 0 14 12"
    width="13"
    height="11"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    style={{ display: "block" }}
    aria-hidden="true"
  >
    <path d="M1 1.5h12M1 6h12M1 10.5h12" />
  </svg>
);

const isInsideSubmenu = (target: EventTarget | null): boolean => {
  const el = target as Element | null;
  return !!(el && typeof el.closest === "function" && el.closest("[data-midas-v3-submenu]"));
};

const ComposerMenuV3: React.FC<ComposerMenuV3Props> = ({
  threadKey,
  disabled = false,
}) => {
  const { translate } = useTranslation("chatBot");

  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    bottom: number;
    right: number;
  } | null>(null);

  // ── Override indicator ──
  // Model: applyThreadModel REMOVES the key for the default model, so key
  // presence = non-default selection.
  const overrideActive = useAppSelector(
    (s) => !!s.midasV3.modelOverrideByThread[threadKey]
  );

  // ── Anchor the popover above the trigger, right-aligned, clamped. ──
  const recomputePosition = useCallback(() => {
    const trig = triggerRef.current;
    if (!trig) return;
    const tr = trig.getBoundingClientRect();
    let right = window.innerWidth - tr.right;
    if (window.innerWidth - right - MENU_WIDTH < VIEWPORT_MARGIN) {
      right = Math.max(
        VIEWPORT_MARGIN,
        window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN
      );
    }
    const bottom = window.innerHeight - tr.top + MENU_OFFSET_Y;
    setPosition({ bottom, right });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    recomputePosition();
    const onWinChange = () => recomputePosition();
    window.addEventListener("resize", onWinChange);
    window.addEventListener("scroll", onWinChange, true);
    return () => {
      window.removeEventListener("resize", onWinChange);
      window.removeEventListener("scroll", onWinChange, true);
    };
  }, [open, recomputePosition]);

  // ── Outside-click close (skipping the nested model submenu) + focus. ──
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => menuRef.current?.focus(), 0);
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      // Clicks inside ModelPickerV3's portaled dropdown are logically INSIDE
      // this popover even though they're outside its DOM.
      if (isInsideSubmenu(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDocMouseDown);
    };
  }, [open]);

  // Focus returns to the trigger on close.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  // Esc closes the popover — UNLESS the event came from inside the nested
  // model submenu (React portals bubble through the REACT tree, so the
  // submenu's keydowns reach here): that Esc belongs to the submenu, which
  // closes itself and returns focus to its trigger inside this popover.
  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== "Escape") return;
    if (isInsideSubmenu(e.target)) return;
    e.preventDefault();
    setOpen(false);
  }, []);

  // `.cc`-only surface: render NOTHING off-`.cc` — no reserved space, the
  // composer row stays pixel-identical (the hosted picker was already
  // `.cc`-gated). All hooks above run unconditionally for stable hook order.
  if (!isCcDevSurface()) return null;

  const triggerAria = (
    overrideActive
      ? translate("midas.v3.composerMenu.triggerAriaActive", {
          defaultValue: "Chat options — model (override active)",
        })
      : translate("midas.v3.composerMenu.triggerAria", {
          defaultValue: "Chat options — model",
        })
  ) as string;
  const menuAria = translate("midas.v3.composerMenu.menuAria", {
    defaultValue: "Model settings for this thread",
  }) as string;
  const modelEyebrow = translate("midas.v3.composerMenu.modelEyebrow", {
    defaultValue: "Model · this thread",
  }) as string;

  const active = hovered || open;

  const menuNode =
    open && typeof document !== "undefined" ? (
      <div
        className={`midas-v3-root ${resolveV3PaletteClass()}`}
        style={{
          position: "fixed",
          inset: 0,
          // Below ModelPickerV3's nested dropdown (1400) so the submenu
          // renders on top of this popover.
          zIndex: 1390,
          background: "transparent",
          pointerEvents: "none",
        }}
        role="presentation"
      >
        <div
          ref={menuRef}
          role="dialog"
          aria-label={menuAria}
          tabIndex={-1}
          onKeyDown={handleMenuKeyDown}
          className="midas-v33-menu-enter"
          style={{
            position: "fixed",
            bottom: position?.bottom ?? -9999,
            right: position?.right ?? -9999,
            width: MENU_WIDTH,
            background: "var(--midas-v3-card)",
            color: "var(--midas-v3-ink)",
            border: "1px solid var(--midas-v3-border)",
            borderRadius: 8,
            padding: 6,
            boxShadow:
              "0 8px 24px rgba(15, 23, 30, 0.10), 0 2px 6px rgba(15, 23, 30, 0.06)",
            fontFamily: "var(--font-sans), Sora, system-ui, sans-serif",
            pointerEvents: "auto",
            outline: "none",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* ── MODEL section — hosts the unchanged ModelPickerV3 ── */}
          <div style={EYEBROW_STYLE}>{modelEyebrow}</div>
          <div style={{ padding: "0 8px 4px" }}>
            <ModelPickerV3 threadKey={threadKey} disabled={disabled} />
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerAria}
        title={triggerAria}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          // Matches the ＋ attach button chrome (30×30, bordered, radius 8)
          // so the row reads as one uniform button cluster: ☰ ＋ ↑.
          width: 30,
          height: 30,
          padding: 0,
          position: "relative",
          flexShrink: 0,
          background: active ? "var(--accent-soft)" : "transparent",
          color: disabled
            ? "var(--faint)"
            : active
            ? "var(--accent)"
            : "var(--muted)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          cursor: disabled ? "not-allowed" : "pointer",
          display: "grid",
          placeItems: "center",
          opacity: disabled ? 0.5 : 1,
          transition:
            "background 0.12s ease, color 0.12s ease, border-color 0.12s ease, opacity 0.12s ease",
        }}
      >
        <HamburgerIcon />
        {/* Override indicator — accent dot, top-right. aria-hidden: the
            state is voiced via triggerAria ("override active"). */}
        {overrideActive && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -3,
              right: -3,
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "var(--accent)",
              border: "1.5px solid var(--bg)",
              pointerEvents: "none",
            }}
          />
        )}
      </button>
      {menuNode ? ReactDOM.createPortal(menuNode, document.body) : null}
    </div>
  );
};

export default ComposerMenuV3;
